import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Paper, Stack, CircularProgress } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import useThemeStyles from '../../../../../../../../hooks/useThemeStyles';
import { useIsVisible } from '../../../../../../../../hooks';
import ChapterCardHeader from '../../../../../ChapterCardHeader';
import EditChapterModal from '../../../../../../../../components/EditChapterModal/EditChapterModal';
import EditSceneModal from '../../../../../../../../components/EditSceneModal/EditSceneModal';
import { SceneBlock, EditorBlock } from './Components';
import SceneActionsDialog from './Components/SceneActionsDialog';
import VersionHistoryModal from '../../../../../../../../components/VersionHistoryModal/VersionHistoryModal';
import { useProjectAccess } from '../../../../../../../../contexts/ProjectAccessContext';
import sceneService from '../../../../../../../../utils/apiRequests/sceneService';
import {
  selectEditorStatesByChapterId,
  setEditorState,
  removeEditorState,
} from '../../../../../../../../features/editor/editorSlice';
import mergeScenes from '../../../../../../../../utils/mergeScenes';
import isSceneContentEmpty from '../../../../../../../../utils/isSceneContentEmpty';
import { updateChapterInProject } from '../../../../../../../../features/projects/projectsSlice';

const ChapterCard = ({
  chapter,
  index,
  onChapterClick = undefined,
  onSceneAdded,
  onAddScene,
  suppressSceneLoad = false,
  isActive = true,
  onVisibilityChange,
}) => {
  const dispatch = useDispatch();
  const { colors, components, isDark } = useThemeStyles();

  // Get the scroll container for intersection observer
  // The manuscript content scrolls within #manuscript-scroll-container, not the viewport
  const [scrollContainer, setScrollContainer] = useState(null);

  useEffect(() => {
    const container = document.getElementById('manuscript-scroll-container');
    if (container) {
      setScrollContainer(container);
    }
  }, []);

  const [ref, isVisible] = useIsVisible({
    threshold: 0, // Chapter is considered visible when ANY part is in the scroll container (prevents infinite loop for long chapters)
    rootMargin: '100px', // Start loading a bit before fully visible
    root: scrollContainer,
  });

  // Track previous visibility to prevent unnecessary callbacks
  const prevIsVisibleRef = useRef(isVisible);

  // Notify parent of visibility changes (only when it actually changes)
  useEffect(() => {
    if (onVisibilityChange && prevIsVisibleRef.current !== isVisible) {
      prevIsVisibleRef.current = isVisible;
      onVisibilityChange(isVisible);
    }
  }, [isVisible, onVisibilityChange]);

  // State for managing scenes
  const [scenes, setScenes] = useState(chapter.scenes || []);
  const [loadingScenes, setLoadingScenes] = useState(false);
  const [scenesLoaded, setScenesLoaded] = useState(false);
  const [chapterDetails, setChapterDetails] = useState(chapter);
  const prevScenesRef = useRef(scenes);

  // Log when scenes are added or removed
  useEffect(() => {
    const prevSceneIds = new Set(prevScenesRef.current.map((s) => s.id || s._id).filter(Boolean));
    const currentSceneIds = new Set(scenes.map((s) => s.id || s._id).filter(Boolean));

    // Find added scenes
    const addedScenes = scenes.filter((s) => !prevSceneIds.has(s.id || s._id));
    // Find removed scenes
    const removedScenes = prevScenesRef.current.filter((s) => !currentSceneIds.has(s.id || s._id));

    if (addedScenes.length > 0) {
      console.log(
        `[ChapterCard] Chapter "${chapterDetails?.name || chapter.id}" - Scenes ADDED:`,
        addedScenes
      );
    }

    if (removedScenes.length > 0) {
      console.log(
        `[ChapterCard] Chapter "${chapterDetails?.name || chapter.id}" - Scenes REMOVED:`,
        removedScenes
      );
    }

    // Update ref for next comparison
    prevScenesRef.current = scenes;
  }, [scenes, chapterDetails?.name, chapter.id]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [sceneToEdit, setSceneToEdit] = useState(null);
  const [sceneActionsContext, setSceneActionsContext] = useState(null);
  const [historyScene, setHistoryScene] = useState(null);
  const { canEdit } = useProjectAccess();
  const chapterElementId = `chapter-${
    chapterDetails?.id || chapterDetails?._id || chapter.id || chapter._id || index + 1
  }`;

  // Drag and drop state for scene divider
  const [draggedSceneIndex, setDraggedSceneIndex] = useState(null);

  /** When incremented, forces Lexical to reload scene.data from props (e.g. after split trims content). */
  const [sceneHydrationNonce, setSceneHydrationNonce] = useState({});

  /** Scene index awaiting click-between-blocks to choose split position; null = idle */
  const [splitPlacementSceneIndex, setSplitPlacementSceneIndex] = useState(null);

  const bumpSceneHydration = useCallback((sceneId) => {
    if (!sceneId) return;
    setSceneHydrationNonce((prev) => ({
      ...prev,
      [sceneId]: (prev[sceneId] || 0) + 1,
    }));
  }, []);

  const handleBeginSplitPlacement = useCallback((sceneIndex) => {
    setSplitPlacementSceneIndex((prev) => (prev === sceneIndex ? null : sceneIndex));
  }, []);

  const handleCancelSplitPlacement = useCallback(() => {
    setSplitPlacementSceneIndex(null);
  }, []);

  const adjustSplitPlacementAfterRemoval = useCallback((removedIndex) => {
    setSplitPlacementSceneIndex((prev) => {
      if (prev === null) return null;
      if (prev === removedIndex) return null;
      if (prev > removedIndex) return prev - 1;
      return prev;
    });
  }, []);

  const editorState = useSelector((state) => selectEditorStatesByChapterId(state, chapter.id));
  const editorStateRef = useRef(editorState);
  editorStateRef.current = editorState;
  const activeProjectId = useSelector((state) => state.projects.activeProject?._id);

  const getLiveEditorJson = useCallback((sceneItem) => {
    const { id, data: sceneData } = sceneItem;
    const slice = id && editorStateRef.current?.[id];
    const rootFromRedux = slice?.root;
    const baseData = typeof sceneData === 'object' && sceneData ? sceneData : {};
    if (rootFromRedux) {
      return { ...baseData, root: rootFromRedux };
    }
    return {
      ...baseData,
      root: baseData.root || { children: [] },
    };
  }, []);

  const initialWordCountRef = useRef(chapter.wordCount || 0);

  useEffect(() => {
    if (chapter?.id) {
      initialWordCountRef.current = chapter.wordCount || 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapter.id]);

  useEffect(() => {
    if (typeof chapter?.wordCount === 'number') {
      initialWordCountRef.current = Math.max(
        initialWordCountRef.current,
        Number(chapter.wordCount) || 0
      );
    }
  }, [chapter.wordCount]);

  const computeMinHeight = useCallback((wordCount) => {
    const WORDS_PER_PAGE = 300;
    const BASE_HEIGHT_IN = 11;
    const safeWordCount = Number(wordCount) || 0;
    const estimatedPages = Math.max(1, Math.ceil(safeWordCount / WORDS_PER_PAGE));
    return `${estimatedPages * BASE_HEIGHT_IN}in`;
  }, []);

  const minHeight = computeMinHeight(
    Math.max(initialWordCountRef.current, Number(chapterDetails?.wordCount) || 0)
  );

  const handleSceneWordCountUpdate = useCallback((sceneId, wordCount) => {
    if (!sceneId) return;

    setScenes((prevScenes) =>
      prevScenes.map((existingScene) => {
        const existingId = existingScene.id || existingScene._id;
        if (existingId === sceneId) {
          return {
            ...existingScene,
            wordCount,
          };
        }
        return existingScene;
      })
    );
  }, []);

  const handleChapterWordCountUpdate = useCallback(
    (wordCount) => {
      const identifier =
        chapterDetails?.id ||
        chapterDetails?._id ||
        chapter.id ||
        chapter._id ||
        chapter?.chapterId;

      initialWordCountRef.current = Math.max(initialWordCountRef.current, Number(wordCount) || 0);

      setChapterDetails((prev) => ({
        ...prev,
        wordCount,
      }));

      if (identifier) {
        dispatch(
          updateChapterInProject({
            projectId: activeProjectId,
            chapterId: identifier,
            updates: {
              wordCount,
            },
          })
        );
      }
    },
    [
      chapterDetails?.id,
      chapterDetails?._id,
      chapter.id,
      chapter._id,
      chapter?.chapterId,
      dispatch,
      activeProjectId,
    ]
  );

  // Function to add a new scene to the list
  const addNewScene = useCallback((newScene) => {
    console.log('Adding new scene to chapter:', newScene);
    setScenes((prevScenes) => [...prevScenes, newScene]);
  }, []);

  // Expose addNewScene function to parent through callback
  useEffect(() => {
    if (onSceneAdded && chapter.id) {
      onSceneAdded(chapter.id, addNewScene);
    }
  }, [chapter.id, onSceneAdded, addNewScene]);

  useEffect(() => {
    setChapterDetails(chapter);
  }, [chapter]);

  // Track previous isActive state
  const prevIsActiveRef = useRef(isActive);
  // Track when scenes were last cleared to prevent rapid clearing/re-fetching
  const lastClearedRef = useRef(null);
  const CLEAR_DEBOUNCE_MS = 1000; // Don't clear scenes if chapter was active within last second

  // Clear scenes when chapter becomes inactive (with debounce to prevent infinite loops)
  useEffect(() => {
    const wasActive = prevIsActiveRef.current;
    const isNowInactive = !isActive && wasActive;

    if (isNowInactive && scenes.length > 0) {
      const now = Date.now();
      const timeSinceLastClear = lastClearedRef.current ? now - lastClearedRef.current : Infinity;

      // Only clear if enough time has passed since last clear (prevents rapid clearing/re-fetching)
      if (timeSinceLastClear > CLEAR_DEBOUNCE_MS) {
        console.log(
          `[ChapterCard] Chapter "${chapterDetails?.name || chapter.id}" became inactive - clearing scenes`
        );
        setScenes([]);
        setScenesLoaded(false);
        setLoadingScenes(false);
        lastClearedRef.current = now;
      } else {
        console.log(
          `[ChapterCard] Chapter "${chapterDetails?.name || chapter.id}" became inactive but skipping clear (debounced, ${timeSinceLastClear}ms since last clear)`
        );
      }
    }

    prevIsActiveRef.current = isActive;
  }, [isActive, scenes.length, chapterDetails?.name, chapter.id]);

  // Fetch scenes when chapter becomes visible and active
  useEffect(() => {
    const fetchScenes = async () => {
      // Only load scenes if chapter is active
      if (
        suppressSceneLoad ||
        !isActive ||
        !isVisible ||
        !chapter.id ||
        scenesLoaded ||
        loadingScenes
      ) {
        return;
      }

      setLoadingScenes(true);
      try {
        const response = await sceneService.getScenesByChapter(chapter.id);

        // Extract scenes from Redux editor state (ref = latest; avoids effect re-running on each save)
        const latestEditorState = editorStateRef.current;
        const reduxScenes = latestEditorState
          ? Object.values(latestEditorState)
              .map((state) => state.scene || state)
              .filter(Boolean)
          : [];

        // Merge scenes from Redux and API response
        let mergedScenes = [];
        if (response?.data?.scenes) {
          mergedScenes = mergeScenes(reduxScenes, response.data.scenes, {
            pruneReduxOnlyNotInApi: response.fromCache === false,
          });
        } else if (reduxScenes.length > 0) {
          // If no API response but we have Redux scenes, use those
          mergedScenes = reduxScenes;
        }

        setScenes(mergedScenes);
        setScenesLoaded(true);
      } catch (error) {
        console.error('Error fetching scenes for chapter:', chapter.id, error);

        // On error, try to use Redux state as fallback
        const latestEditorState = editorStateRef.current;
        const reduxScenes = latestEditorState
          ? Object.values(latestEditorState)
              .map((state) => state.scene || state)
              .filter(Boolean)
          : [];
        setScenes(reduxScenes.length > 0 ? reduxScenes : []);
        setScenesLoaded(true);
      } finally {
        setLoadingScenes(false);
      }
    };

    fetchScenes();
    // Intentionally omit `editorState`: refetching on every save replaced local Lexical JSON via merge.
    // Latest Redux editor slices are merged via editorStateRef inside fetchScenes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suppressSceneLoad, isActive, isVisible, chapter, index, scenesLoaded, loadingScenes]);

  // Console log when chapter becomes visible
  useEffect(() => {
    if (isVisible) {
      // console.log('Current visible chapter:', {
      //   chapterName: chapter.name || `Chapter ${index + 1}`,
      //   chapterIndex: index,
      //   chapterId: chapter.id,
      //   wordCount: chapter.wordCount || 0,
      //   sceneCount: chapter.sceneCount || 0,
      //   scenes,
      //   scenesLoaded,
      // });
    }
  }, [isVisible, chapter, index, scenes, scenesLoaded]);

  // Drag and drop handlers for scene divider
  const handleDragStart = (e, sceneIndex) => {
    console.log('Starting drag of scene divider:', sceneIndex);
    setDraggedSceneIndex(sceneIndex);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('sceneIndex', sceneIndex);
  };

  const handleDragEnd = () => {
    console.log('Drag ended');
    setDraggedSceneIndex(null);
  };

  // Handler for when dragging over an editor
  const handleEditorDragOver = (e, editorIndex, nodeIndex) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedSceneIndex !== null) {
      // Only allow dropping into adjacent editors
      const isAdjacentEditor =
        editorIndex === draggedSceneIndex || // Same scene's editor (below the divider)
        editorIndex === draggedSceneIndex - 1; // Previous scene's editor (above the divider)

      if (isAdjacentEditor) {
        e.dataTransfer.dropEffect = 'move';
        console.log('Dragging over valid adjacent editor:', {
          draggedSceneIndex,
          editorIndex,
          nodeIndex,
        });
      } else {
        e.dataTransfer.dropEffect = 'none';
        console.log('Not an adjacent editor, drop not allowed:', {
          draggedSceneIndex,
          editorIndex,
        });
      }
    }
  };

  // Handler for dropping divider on editor
  const handleEditorDrop = async (e, targetEditorIndex, nodeIndex) => {
    e.preventDefault();
    e.stopPropagation();

    console.log('Dropped divider on editor:', {
      draggedSceneIndex,
      targetEditorIndex,
      nodeIndex,
    });

    if (draggedSceneIndex === null) {
      setDraggedSceneIndex(null);
      return;
    }

    // Only allow dropping into adjacent editors
    const isAdjacentEditor =
      targetEditorIndex === draggedSceneIndex || // Same scene's editor (below the divider)
      targetEditorIndex === draggedSceneIndex - 1; // Previous scene's editor (above the divider)

    if (!isAdjacentEditor) {
      console.warn('Cannot drop on non-adjacent editor');
      setDraggedSceneIndex(null);
      return;
    }

    try {
      const targetScene = scenes[targetEditorIndex];
      const draggedScene = scenes[draggedSceneIndex];

      if (!targetScene || !draggedScene) {
        console.error('Invalid scene indices');
        return;
      }

      console.log('Splitting content:', {
        targetScene: targetScene.name,
        draggedScene: draggedScene.name,
        nodeIndex,
      });

      // Get the target scene's content
      const targetContent = targetScene.data?.root?.children || [];

      if (targetContent.length === 0 || nodeIndex >= targetContent.length) {
        console.warn('No content to split or invalid nodeIndex');
        setDraggedSceneIndex(null);
        return;
      }

      // Split the content at nodeIndex
      const remainingInTarget = targetContent.slice(0, nodeIndex);
      const movingContent = targetContent.slice(nodeIndex);

      console.log('Split result:', {
        originalCount: targetContent.length,
        remainingCount: remainingInTarget.length,
        movingCount: movingContent.length,
      });

      // The scene header is the divider BETWEEN the current scene and the next
      // When dragging the divider:
      // - Drop on current scene's editor (draggedSceneIndex): moves content to next scene
      // - Drop on previous scene's editor (draggedSceneIndex - 1): moves content to current scene

      let receivingSceneIndex;
      let receivingScene;
      let receivingContent;

      if (targetEditorIndex === draggedSceneIndex - 1) {
        // Dropped on previous scene's editor: content moves from previous -> current
        receivingSceneIndex = draggedSceneIndex;
        receivingScene = draggedScene;
        const currentContent = draggedScene.data?.root?.children || [];
        // Prepend moved content to the beginning of current scene
        receivingContent = [...movingContent, ...currentContent];
        console.log(
          'Moving content from Scene',
          targetEditorIndex,
          'to Scene',
          receivingSceneIndex
        );
      } else {
        // Dropped on current scene's editor: content moves from current -> next (or new scene)
        receivingSceneIndex = draggedSceneIndex + 1;
        if (receivingSceneIndex >= scenes.length) {
          const timestamp = Date.now();
          const trimmedTarget = {
            ...targetScene,
            updatedAt: timestamp,
            data: {
              ...targetScene.data,
              root: {
                ...targetScene.data?.root,
                children: remainingInTarget,
              },
            },
          };

          const movingRoot = {
            ...targetScene.data?.root,
            children: movingContent,
          };

          try {
            const createRes = await sceneService.addScene(chapter.id, {
              type: draggedScene.type || 'paragraph',
              name: '',
              data: {
                root: movingRoot,
              },
            });

            if (createRes?.error || !createRes?.data?.scene) {
              console.error('Could not create scene when splitting:', createRes?.error);
              return;
            }

            const created = createRes.data.scene;
            const newSceneRow = {
              ...created,
              chapterId: chapter.id,
              data: { root: movingRoot },
            };

            const updatedScenes = [...scenes];
            updatedScenes[targetEditorIndex] = trimmedTarget;
            updatedScenes.splice(receivingSceneIndex, 0, newSceneRow);

            setScenes(updatedScenes);

            dispatch(
              setEditorState({
                id: trimmedTarget.id,
                root: trimmedTarget.data.root,
                chapterId: trimmedTarget.chapterId ?? chapter.id,
                scene: trimmedTarget,
              })
            );
            dispatch(
              setEditorState({
                id: newSceneRow.id,
                root: movingRoot,
                chapterId: newSceneRow.chapterId ?? chapter.id,
                scene: newSceneRow,
              })
            );

            const remainingEditorJson = {
              ...(typeof targetScene.data === 'object' ? targetScene.data : {}),
              root: trimmedTarget.data.root,
            };
            await sceneService.putEditorState(trimmedTarget.id, remainingEditorJson, {
              sceneDocForCache: { ...trimmedTarget, data: remainingEditorJson },
            });
            bumpSceneHydration(trimmedTarget.id);
          } catch (createErr) {
            console.error('Error creating scene from divider drop:', createErr);
          }
          return;
        }
        receivingScene = scenes[receivingSceneIndex];
        const nextContent = receivingScene.data?.root?.children || [];
        // Prepend moved content to the beginning of next scene
        receivingContent = [...movingContent, ...nextContent];
        console.log(
          'Moving content from Scene',
          targetEditorIndex,
          'to Scene',
          receivingSceneIndex
        );
      }

      // Update both scenes with new content
      const updatedScenes = [...scenes];
      const timestamp = Date.now();

      // Update the scene we split from (keeping only content before split)
      updatedScenes[targetEditorIndex] = {
        ...targetScene,
        updatedAt: timestamp,
        data: {
          ...targetScene.data,
          root: {
            ...targetScene.data?.root,
            children: remainingInTarget,
          },
        },
      };

      // Update the scene receiving the moved content
      updatedScenes[receivingSceneIndex] = {
        ...receivingScene,
        updatedAt: timestamp,
        data: {
          ...receivingScene.data,
          root: {
            ...receivingScene.data?.root,
            children: receivingContent,
          },
        },
      };

      // Update local state
      setScenes(updatedScenes);

      // Update Redux store with new editor states
      dispatch(
        setEditorState({
          id: targetScene.id,
          root: updatedScenes[targetEditorIndex].data.root,
          chapterId: targetScene.chapterId,
          scene: updatedScenes[targetEditorIndex],
        })
      );

      dispatch(
        setEditorState({
          id: receivingScene.id,
          root: updatedScenes[receivingSceneIndex].data.root,
          chapterId: receivingScene.chapterId,
          scene: updatedScenes[receivingSceneIndex],
        })
      );

      console.log('Scenes updated locally and in Redux, persisting to backend...');

      try {
        const targetEditorJson = {
          ...(typeof targetScene.data === 'object' ? targetScene.data : {}),
          root: updatedScenes[targetEditorIndex].data.root,
        };
        const receivingEditorJson = {
          ...(typeof receivingScene.data === 'object' ? receivingScene.data : {}),
          root: updatedScenes[receivingSceneIndex].data.root,
        };
        const [putTarget, putReceiving] = await Promise.all([
          sceneService.putEditorState(targetScene.id, targetEditorJson, {
            sceneDocForCache: { ...updatedScenes[targetEditorIndex], data: targetEditorJson },
          }),
          sceneService.putEditorState(receivingScene.id, receivingEditorJson, {
            sceneDocForCache: { ...updatedScenes[receivingSceneIndex], data: receivingEditorJson },
          }),
        ]);

        if (!putTarget?.error && !putReceiving?.error) {
          bumpSceneHydration(targetScene.id);
          bumpSceneHydration(receivingScene.id);
        }

        console.log('Successfully persisted scene content changes:', {
          targetScene: putTarget,
          receivingScene: putReceiving,
        });
      } catch (apiError) {
        console.error('Error persisting scene changes:', apiError);
      }
    } catch (error) {
      console.error('Error splitting scene content:', error);
    } finally {
      setDraggedSceneIndex(null);
    }
  };

  const handleSplitScene = useCallback(
    async (editorIndex, { remainingStateJson, movingStateJson }) => {
      const movingChildren = movingStateJson?.root?.children;
      if (!movingChildren?.length) return;

      const targetScene = scenes[editorIndex];
      if (!targetScene?.id || !chapter?.id) return;

      const timestamp = Date.now();

      const trimmedTarget = {
        ...targetScene,
        updatedAt: timestamp,
        data: {
          ...targetScene.data,
          root: remainingStateJson.root,
        },
      };

      try {
        const putRes = await sceneService.putEditorState(targetScene.id, remainingStateJson, {
          sceneDocForCache: { ...trimmedTarget, data: remainingStateJson },
        });
        if (putRes?.error) {
          console.error('Could not save trimmed scene:', putRes.error);
          return;
        }

        const createRes = await sceneService.addScene(chapter.id, {
          type: targetScene.type || 'paragraph',
          name: '',
          data: {
            root: movingStateJson.root,
          },
        });

        if (createRes?.error || !createRes?.data?.scene) {
          console.error('Could not create scene when splitting:', createRes?.error);
          return;
        }

        const created = createRes.data.scene;
        const newSceneRow = {
          ...created,
          chapterId: chapter.id,
          data: movingStateJson,
        };

        const updatedScenes = [...scenes];
        updatedScenes[editorIndex] = trimmedTarget;
        updatedScenes.splice(editorIndex + 1, 0, newSceneRow);

        setScenes(updatedScenes);

        dispatch(
          setEditorState({
            id: trimmedTarget.id,
            root: trimmedTarget.data.root,
            chapterId: trimmedTarget.chapterId ?? chapter.id,
            scene: trimmedTarget,
          })
        );
        dispatch(
          setEditorState({
            id: newSceneRow.id,
            root: movingStateJson.root,
            chapterId: newSceneRow.chapterId ?? chapter.id,
            scene: newSceneRow,
          })
        );

        bumpSceneHydration(trimmedTarget.id);
        setSplitPlacementSceneIndex(null);
      } catch (err) {
        console.error('Split scene error:', err);
      }
    },
    [bumpSceneHydration, chapter.id, dispatch, scenes]
  );

  const handleMergeIntoPrevious = useCallback(async () => {
    if (!sceneActionsContext || sceneActionsContext.sceneIndex < 1) return;
    const { sceneIndex } = sceneActionsContext;
    const prevScene = scenes[sceneIndex - 1];
    const currScene = scenes[sceneIndex];
    if (!prevScene?.id || !currScene?.id) return;

    setSceneActionsContext(null);

    const prevJson = getLiveEditorJson(prevScene);
    const currJson = getLiveEditorJson(currScene);
    const prevChildren = prevJson.root?.children || [];
    const currChildren = currJson.root?.children || [];
    const mergedEditorJson = {
      ...prevJson,
      root: {
        ...(prevJson.root || {}),
        children: [...prevChildren, ...currChildren],
      },
    };

    const timestamp = Date.now();
    let mergedPrevRow = {
      ...prevScene,
      updatedAt: timestamp,
      data: mergedEditorJson,
      wordCount: (prevScene.wordCount || 0) + (currScene.wordCount || 0),
    };

    try {
      const putRes = await sceneService.putEditorState(prevScene.id, mergedEditorJson, {
        sceneDocForCache: {
          ...mergedPrevRow,
          _id: prevScene._id,
          data: mergedEditorJson,
        },
      });
      if (putRes?.error) {
        console.error('Could not merge into previous scene (save):', putRes.error);
        return;
      }
      if (typeof putRes?.data?.scene?.wordCount === 'number') {
        mergedPrevRow = { ...mergedPrevRow, wordCount: putRes.data.scene.wordCount };
      }

      const delRes = await sceneService.deleteScene(currScene);
      if (delRes?.error) {
        console.error('Could not delete scene after merge:', delRes.error);
        return;
      }

      if (currScene.id) dispatch(removeEditorState(currScene.id));
      if (currScene._id && String(currScene._id) !== String(currScene.id ?? '')) {
        dispatch(removeEditorState(String(currScene._id)));
      }

      setScenes((prev) => {
        const next = [...prev];
        next[sceneIndex - 1] = mergedPrevRow;
        next.splice(sceneIndex, 1);
        return next;
      });

      dispatch(
        setEditorState({
          id: prevScene.id,
          root: mergedEditorJson.root,
          chapterId: prevScene.chapterId ?? chapter.id,
          scene: mergedPrevRow,
        })
      );
      bumpSceneHydration(prevScene.id);
      adjustSplitPlacementAfterRemoval(sceneIndex);

      setChapterDetails((p) => ({
        ...p,
        sceneCount: Math.max(0, (p.sceneCount != null ? p.sceneCount : scenes.length) - 1),
      }));
    } catch (err) {
      console.error('Merge into previous scene error:', err);
    }
  }, [
    sceneActionsContext,
    scenes,
    getLiveEditorJson,
    chapter.id,
    dispatch,
    bumpSceneHydration,
    adjustSplitPlacementAfterRemoval,
  ]);

  const deleteSceneAtIndex = useCallback(
    async (sceneIndex) => {
      const currScene = scenes[sceneIndex];
      if (!currScene?.id && !currScene?._id) return;

      try {
        const delRes = await sceneService.deleteScene(currScene);
        if (delRes?.error) {
          console.error('Delete scene failed:', delRes.error);
          return;
        }

        if (currScene.id) dispatch(removeEditorState(currScene.id));
        if (currScene._id && String(currScene._id) !== String(currScene.id ?? '')) {
          dispatch(removeEditorState(String(currScene._id)));
        }

        setScenes((prev) => prev.filter((_, i) => i !== sceneIndex));
        adjustSplitPlacementAfterRemoval(sceneIndex);

        setChapterDetails((p) => ({
          ...p,
          sceneCount: Math.max(0, (p.sceneCount != null ? p.sceneCount : scenes.length) - 1),
        }));
      } catch (err) {
        console.error('Delete scene error:', err);
      }
    },
    [scenes, dispatch, adjustSplitPlacementAfterRemoval]
  );

  const handleDeleteSceneFromHeader = useCallback(async () => {
    if (!sceneActionsContext) return;
    const { sceneIndex } = sceneActionsContext;
    setSceneActionsContext(null);
    await deleteSceneAtIndex(sceneIndex);
  }, [sceneActionsContext, deleteSceneAtIndex]);

  const handleRequestSceneActions = useCallback(
    (sceneIndex, scene) => {
      if (isSceneContentEmpty(scene, getLiveEditorJson(scene))) {
        deleteSceneAtIndex(sceneIndex);
        return;
      }
      setSceneActionsContext({ sceneIndex, scene });
    },
    [getLiveEditorJson, deleteSceneAtIndex]
  );

  const renderScene = (sceneItem, sceneIndex) => {
    const sceneId = sceneItem.id || sceneItem._id || `scene-${sceneIndex}`;
    const sceneElementId = `scene-${sceneId}`;
    // Store the MongoDB _id for searching (navigation uses _id, but DOM uses id)
    const sceneMongoId = sceneItem._id || sceneItem.id;

    return (
      <Box
        key={sceneItem.id || sceneIndex}
        id={sceneElementId}
        data-scene-mongo-id={sceneMongoId}
        sx={{
          mb: 1,
          opacity: draggedSceneIndex === sceneIndex ? 0.6 : 1,
          transition: 'opacity 0.2s ease',
        }}
      >
        <SceneBlock
          scene={sceneItem}
          sceneIndex={sceneIndex}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          isDraggable={sceneIndex > 0}
          onBeginSplitPlacement={canEdit ? handleBeginSplitPlacement : undefined}
          splitPlacementActive={splitPlacementSceneIndex === sceneIndex}
          onOpenSceneActions={canEdit ? handleRequestSceneActions : undefined}
          onOpenHistory={(idx, scene) => setHistoryScene(scene)}
        />
        {/* Lexical Editor for this scene */}
        <Box sx={{ mt: 1, ml: 2 }}>
          <EditorBlock
            scene={sceneItem}
            editorIndex={sceneIndex}
            onDragOver={handleEditorDragOver}
            onDrop={handleEditorDrop}
            isDragging={draggedSceneIndex !== null}
            onSplitScene={handleSplitScene}
            hydrationNonce={sceneHydrationNonce[sceneItem.id] ?? 0}
            splitPlacementActive={splitPlacementSceneIndex === sceneIndex}
            onCancelSplitPlacement={handleCancelSplitPlacement}
            onSceneWordCountUpdate={handleSceneWordCountUpdate}
            onChapterWordCountUpdate={handleChapterWordCountUpdate}
          />
        </Box>
      </Box>
    );
  };

  const handleSceneMetadataUpdated = useCallback(
    (updatedScene) => {
      if (!updatedScene) return;
      const id = updatedScene.id || updatedScene._id;
      setScenes((prev) =>
        prev.map((s) => ((s.id || s._id) === id ? { ...s, ...updatedScene } : s))
      );

      const editorKey = updatedScene.id;
      if (editorKey && editorStateRef.current?.[editorKey]) {
        const slice = editorStateRef.current[editorKey];
        dispatch(
          setEditorState({
            ...slice,
            scene: {
              ...(slice && typeof slice.scene === 'object' ? slice.scene : {}),
              ...updatedScene,
            },
          })
        );
      }
    },
    [dispatch]
  );

  const handleChapterUpdated = (updatedChapter) => {
    const identifier = updatedChapter?.id || updatedChapter?._id || chapter.id;

    setChapterDetails((prev) => ({
      ...prev,
      ...updatedChapter,
    }));

    if (identifier) {
      dispatch(
        updateChapterInProject({
          projectId: activeProjectId,
          chapterId: identifier,
          updates: {
            name: updatedChapter?.name,
            description: updatedChapter?.description,
          },
        })
      );
    }
  };

  return (
    <>
      <Paper
        id={chapterElementId}
        ref={ref}
        elevation={0}
        sx={{
          p: 0,
          backgroundColor: isDark ? '#1e293b' : colors.paper.background,
          backgroundImage: isDark ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)' : 'none',
          border: components.card.border,
          borderRadius: 2,
          cursor: 'default',
          opacity: isVisible ? 1 : 0.7, // Visual feedback for visibility
          overflow: 'hidden',
          minHeight,
          // For inactive chapters, use fixed height to prevent resizing
          ...(isActive ? {} : { height: minHeight, maxHeight: minHeight }),
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => {
          // Don't trigger chapter click if the click originated from an editor or scene block
          // This prevents unwanted scrolling when clicking into scene editors
          const { target } = e;
          const isEditorClick =
            target.closest('.editor-text-formatting') ||
            target.closest('[contenteditable="true"]') ||
            target.closest('[data-lexical-editor="true"]');

          if (!isEditorClick && onChapterClick) {
            onChapterClick(chapterDetails);
          }
        }}
      >
        <Stack spacing={0} sx={{ flexGrow: 1 }}>
          <ChapterCardHeader
            chapterName={chapterDetails?.name}
            chapterNumber={index + 1}
            description={chapterDetails?.description}
            wordCount={chapterDetails?.wordCount}
            onEdit={() => setIsEditModalOpen(true)}
          />

          <Box sx={{ px: 3, pb: 3 }}>
            {!isActive ? (
              // Placeholder for inactive chapters - maintains height but doesn't load content
              <Box
                sx={{
                  height: '100%',
                  minHeight: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexGrow: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: isDark ? '#cbd5e1' : colors.text.secondary,
                    fontSize: '0.875rem',
                    fontStyle: 'italic',
                    opacity: 0.5,
                  }}
                >
                  Chapter content not loaded
                </Typography>
              </Box>
            ) : (
              <>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                  {chapterDetails?.sceneCount && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: isDark ? '#cbd5e1' : colors.text.secondary,
                      }}
                    >
                      {chapterDetails.sceneCount} scenes
                    </Typography>
                  )}
                </Stack>

                {/* Scenes List */}
                <Box sx={{ mt: 3 }}>
                  <Stack spacing={1}>
                    {loadingScenes && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          py: 2,
                        }}
                      >
                        <CircularProgress
                          size={20}
                          sx={{
                            color: colors.primary.main,
                            mr: 1,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: isDark ? '#cbd5e1' : colors.text.secondary,
                            fontSize: '0.75rem',
                          }}
                        >
                          Loading scenes...
                        </Typography>
                      </Box>
                    )}
                    {!loadingScenes && scenes.length > 0 && scenes.map(renderScene)}
                    {!loadingScenes && scenes.length === 0 && scenesLoaded && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: isDark ? '#cbd5e1' : colors.text.secondary,
                          fontSize: '0.75rem',
                          fontStyle: 'italic',
                          py: 1,
                          textAlign: 'center',
                        }}
                      >
                        No scenes found
                      </Typography>
                    )}

                    {/* Add Scene Button */}
                    {!loadingScenes && scenesLoaded && onAddScene && canEdit && (
                      <Box
                        sx={{
                          mt: 2,
                          pt: 2,
                          borderTop: `1px dashed ${colors.border.subtle}`,
                        }}
                      >
                        <Box
                          onClick={() => onAddScene(chapter.id)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            py: 1.5,
                            px: 2,
                            borderRadius: 1,
                            border: `1px dashed ${colors.border.subtle}`,
                            backgroundColor: isDark ? '#1e293b' : colors.paper.background,
                            backgroundImage: isDark
                              ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
                              : 'none',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: isDark ? '#334155' : colors.background.surface,
                              borderColor: colors.primary.main,
                              '& .add-scene-text': {
                                color: colors.primary.main,
                              },
                            },
                          }}
                        >
                          <Typography
                            className="add-scene-text"
                            variant="body2"
                            sx={{
                              color: isDark ? '#cbd5e1' : colors.text.secondary,
                              fontSize: '0.875rem',
                              fontWeight: 'medium',
                              transition: 'color 0.2s ease',
                            }}
                          >
                            + Add Scene
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Stack>
                </Box>
              </>
            )}
          </Box>
        </Stack>
      </Paper>

      <EditChapterModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        chapter={chapterDetails}
        onChapterUpdated={handleChapterUpdated}
      />

      {React.createElement(EditSceneModal, {
        open: Boolean(sceneToEdit),
        onClose: () => setSceneToEdit(null),
        scene: sceneToEdit,
        chapterName: chapterDetails?.name || '',
        onSceneUpdated: handleSceneMetadataUpdated,
      })}

      <SceneActionsDialog
        open={Boolean(sceneActionsContext)}
        onClose={() => setSceneActionsContext(null)}
        sceneTitle={sceneActionsContext?.scene?.name}
        canMergeIntoPrevious={Boolean(sceneActionsContext && sceneActionsContext.sceneIndex > 0)}
        onMergeIntoPrevious={handleMergeIntoPrevious}
        onDeleteScene={handleDeleteSceneFromHeader}
      />
      <VersionHistoryModal
        open={Boolean(historyScene)}
        onClose={() => setHistoryScene(null)}
        scene={historyScene}
        onRestored={(restoredScene) => {
          if (!restoredScene) return;
          // Swap in the restored content and force the open editor to rehydrate
          // so a later autosave can't resurrect the pre-restore text.
          setScenes((prev) =>
            prev.map((s) =>
              (restoredScene.id && s.id === restoredScene.id) ||
              (restoredScene._id && s._id === restoredScene._id)
                ? { ...s, data: restoredScene.data, wordCount: restoredScene.wordCount }
                : s
            )
          );
          bumpSceneHydration(restoredScene.id);
        }}
      />
    </>
  );
};

/* eslint-disable react/require-default-props */
ChapterCard.propTypes = {
  chapter: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    chapterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    wordCount: PropTypes.number,
    sceneCount: PropTypes.number,
    scenes: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        wordCount: PropTypes.number,
        children: PropTypes.arrayOf(
          PropTypes.shape({
            text: PropTypes.string,
            type: PropTypes.string,
          })
        ),
      })
    ),
  }).isRequired,
  index: PropTypes.number.isRequired,
  onChapterClick: PropTypes.func,
  onSceneAdded: PropTypes.func,
  onAddScene: PropTypes.func,
  suppressSceneLoad: PropTypes.bool,
  isActive: PropTypes.bool,
  onVisibilityChange: PropTypes.func,
};
/* eslint-enable react/require-default-props */

export default ChapterCard;
